import SLSEstoqueClient from '@/api/sls-estoque.client';
import store from '@/store';
import { isPerfilClienteAtivo, isPerfilRepresentanteAtivo } from '@/libs/perfilCliente';
import { getTenantSlug, getImpersonateTenant } from '@/services/tenant';

// Cache por tenant/finalidade, com validade curta para não reter saldo zero antigo.
const SESSION_CACHE_KEY = 'estoque_cache_v3';
const CACHE_TTL_MS = 60 * 1000;

// Tipo de pedido de doação (mesmo código do backend).
const TIPO_PEDIDO_DOACAO = 'WD';

/**
 * Lê o tipo de pedido do pedido em edição.
 * 'WD' → estoque de doação (depósito DOA); demais → venda (LUI/LUN).
 * Cobre o fluxo Home (tipoPedidoCliente) E Operações → Pedidos de Venda
 * (tipoPedido / capa do pedido em intensPedido) — sem o fallback, pedido
 * de doação em Operações consultava o estoque de venda.
 */
export function getTipoPedidoAtual() {
  try {
    const form = store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
    return form?.tipoPedidoCliente?.tipopedidoid
      || form?.tipoPedido?.tipopedidoid
      || store.getters['intensPedido/getFormData']?.dataCapaPedido?.tipopedidoid
      || form?.dataCapaPedido?.tipopedidoid
      || null;
  } catch {
    return null;
  }
}

function getCacheNamespace(tipoPedidoId) {
  const purpose = String(tipoPedidoId || '').toUpperCase() === TIPO_PEDIDO_DOACAO ? 'doacao' : 'venda';
  return `${getImpersonateTenant() || getTenantSlug()}:${purpose}`;
}

function getCache(ns) {
  try {
    const all = JSON.parse(sessionStorage.getItem(SESSION_CACHE_KEY) || '{}');
    return Object.fromEntries(Object.entries(all[ns] || {}).filter(
      ([, item]) => item && Date.now() - item._cachedAt < CACHE_TTL_MS,
    ));
  } catch {
    return {};
  }
}

function setCache(ns, data) {
  try {
    const all = JSON.parse(sessionStorage.getItem(SESSION_CACHE_KEY) || '{}');
    all[ns] = data;
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(all));
  } catch {
  }
}

export default class SLSEstoqueService {
  constructor() {
    this.client = new SLSEstoqueClient();
  }

  /**
   * Consulta estoque para uma lista de item_ids.
   * Resultados são cacheados na sessão.
   * Retorna: { [item_id]: { quantidade, fonte, substituto, quantidadeSubstituto, dataprevista } }
   */
  async consultarEstoque(itemIds, options = {}) {
    if (!itemIds || itemIds.length === 0) return {};
    // `fresh: true` ignora o cache de sessão na LEITURA (busca tudo de novo no
    // TOTVS), mas ainda grava o resultado no cache — assim a tela de detalhe
    // mostra o valor atual e os badges da listagem são atualizados juntos.
    const { fresh = false, tipoPedidoId: tipoPedidoIdInformado = null } = options;

    // Tipo de pedido escolhido no Info. Pedido define o depósito de estoque
    // (WD/doação → DOA; demais → LUI/LUN) e o namespace do cache.
    const tipoPedidoId = tipoPedidoIdInformado || getTipoPedidoAtual();
    const ns = getCacheNamespace(tipoPedidoId);
    const cache = getCache(ns);
    // Considera "missing" também entradas cacheadas sem quantidade
    // (de tentativas anteriores que falharam) — pra re-tentar.
    const missing = itemIds.filter((id) => {
      if (fresh) return true;
      if (!(id in cache)) return true;
      const v = cache[id];
      return !v || v.quantidade === null || v.quantidade === undefined;
    });

    if (missing.length > 0) {
      try {
        const result = await this.client.consultarEstoque(missing, tipoPedidoId);
        const fetched = result.data || {};
        // Só cacheia entradas com quantidade definida (evita persistir falhas
        // — o usuário pode reabrir o modal e tentar de novo).
        Object.keys(fetched).forEach((k) => {
          const v = fetched[k];
          if (v && (v.quantidade !== null && v.quantidade !== undefined)) {
            cache[k] = { ...v, _cachedAt: Date.now() };
          }
        });
        setCache(ns, cache);
        // Retorna direto a resposta atual (mesmo entradas sem quantidade)
        // pra UI mostrar o que veio agora — só não persiste em cache.
        const out = {};
        itemIds.forEach((id) => {
          out[id] = (id in fetched) ? fetched[id] : (cache[id] || null);
        });
        return out;
      } catch (err) {
        console.warn('[SLSEstoqueService] Erro ao consultar estoque:', err);
      }
    }

    const out = {};
    itemIds.forEach(id => {
      out[id] = cache[id] || null;
    });
    return out;
  }

  /**
   * Limpa o cache de estoque da sessão.
   */
  limparCache() {
    sessionStorage.removeItem(SESSION_CACHE_KEY);
  }

  /**
   * Formata o valor de estoque para exibição na UI.
   * Retorna { label, variant, substituto }
   */
  static formatarEstoque(info) {
    // Perfil Cliente não vê o NÚMERO do estoque — só se está disponível ou não.
    if (isPerfilClienteAtivo()) {
      return SLSEstoqueService._formatarEstoqueCliente(info);
    }
    if (isPerfilRepresentanteAtivo() && info
      && Number.isFinite(Number(info.quantidade)) && Number(info.quantidade) > 50) {
      return { label: 'Disponível', variant: 'success', substituto: null };
    }
    if (isPerfilRepresentanteAtivo() && info?.substituto
      && Number(info.quantidadeSubstituto) > 50) {
      return { ...SLSEstoqueService._formatarEstoqueInterno(info), quantidadeSubstituto: null };
    }
    return SLSEstoqueService._formatarEstoqueInterno(info);
  }

  static _formatarEstoqueCliente(info) {
    if (!info) return { label: '—', variant: 'secondary', substituto: null };
    const qty = info.quantidade;
    if (qty === 'INF') return { label: 'Disponível', variant: 'success', substituto: null };
    if (qty === null || qty === undefined) return { label: 'Sem info', variant: 'secondary', substituto: null };
    if (qty === 0 && !info.substituto) return { label: 'Indisponível', variant: 'danger', substituto: null };
    return { label: 'Disponível', variant: 'success', substituto: null };
  }

  static _formatarEstoqueInterno(info) {
    if (!info) return { label: '—', variant: 'secondary', substituto: null };

    if (info.quantidade === 'INF') {
      return { label: 'Certificado', variant: 'success', substituto: null };
    }

    const qty = info.quantidade;

    if (qty === null || qty === undefined) {
      return { label: 'Sem info', variant: 'secondary', substituto: null };
    }

    if (qty === 0) {
      if (info.substituto) {
        return {
          label: `Via substituto: ${info.substituto}`,
          variant: 'warning',
          substituto: info.substituto,
          quantidadeSubstituto: info.quantidadeSubstituto,
          dataprevista: info.dataprevista,
        };
      }
      return {
        label: info.dataprevista ? `Prev: ${info.dataprevista}` : 'Sem estoque',
        variant: 'danger',
        substituto: null,
        dataprevista: info.dataprevista,
      };
    }

    if (qty <= 10) {
      return { label: `${qty} un`, variant: 'warning', substituto: null };
    }

    return { label: `${qty} un`, variant: 'success', substituto: null };
  }

  async importarCsv(file) {
    return this.client.importarCsv(file);
  }
}
